import { useTranslation } from 'react-i18next'

import { CategoricalSharePlotView, quickStudyCategoricalGridAdapter } from '@digital-twins/deck-ui/plots/categorical-share'
import { HeatGridPlot, quickStudyHeatGridAdapter, quickStudyScorecardAdapter } from '@digital-twins/deck-ui/plots/heat-grid'
import { RadarPlot, RadarPlotData } from '@digital-twins/deck-ui/plots/radar'

import { Result } from './quickStudyTypes'

import { PlotStatView } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * A quick-study rating grid drawn from Python's `heat_grid_plot` (W6-QS): the
 * survey tab's `heat-grid` (values, heat, strongest marks and the Average |
 * Top N views all stamped), or its stamped `radar` alternative. The card's
 * chart switch picks the plot; a grid Python found no radar for (fewer than
 * three measures, mixed scales, too many rows) draws as the heat grid, the
 * same read. When the card holds the metric switch (`controlled`), the stat
 * view is the card's and the grid draws no tabs of its own. A categorical grid
 * draws its stamped `stacked-100-bars`, each segment with its share and its
 * people (the card always printed both), and Other opens in place. Returns null
 * when the result carries no plot (stored results).
 *
 * SC: a monadic scorecard's `scorecard_plot` (dimensions down, items across,
 * the gap column) is read first; it is the scorecard's plot even when the
 * pooled `heat_grid_plot` is also stamped.
 */

export type QuickStudyGridView = 'heat-grid' | 'radar'

export type QuickStudyGridResult = Pick<Result, 'heat_grid_plot' | 'stacked_100_bars_plot' | 'scorecard_plot'>

export interface QuickStudyGridPlotProps {
  result: QuickStudyGridResult
  view: QuickStudyGridView
  /** The stat view: the card's metric when `controlled`, else the payload's default. */
  statView?: PlotStatView | null
  controlled?: boolean
  testIdPrefix?: string
}

/** Whether a result can draw through `QuickStudyGridPlot` (a stamped grid is present). */
export function hasQuickStudyGridPlot(result: QuickStudyGridResult): boolean {
  return (
    quickStudyScorecardAdapter.accepts(result) ||
    quickStudyHeatGridAdapter.accepts(result) ||
    quickStudyCategoricalGridAdapter.accepts(result)
  )
}

/** Whether a monadic scorecard carries Python's `scorecard_plot` (SC). */
export function hasQuickStudyScorecardPlot(result: Pick<Result, 'scorecard_plot'>): boolean {
  return quickStudyScorecardAdapter.accepts(result)
}

/** Whether the scorecard's stamped plot carries a radar alternative (SC: the card offers Radar only then). */
export function hasQuickStudyScorecardRadar(result: Pick<Result, 'scorecard_plot'>): boolean {
  return quickStudyScorecardAdapter.accepts(result) && radarOf(quickStudyScorecardAdapter.read(result)) !== null
}

/** The stamped heat grid to draw: the scorecard's own plot first, else the pooled grid. */
export function stampedHeatGridOf(
  result: QuickStudyGridResult,
): ReturnType<typeof quickStudyHeatGridAdapter.read> | null {
  if (quickStudyScorecardAdapter.accepts(result)) {
    return quickStudyScorecardAdapter.read(result)
  }
  return quickStudyHeatGridAdapter.accepts(result) ? quickStudyHeatGridAdapter.read(result) : null
}

function radarOf(plot: { alternatives?: ReadonlyArray<{ kind: string; data: unknown }> }): RadarPlotData | null {
  const alternative = (plot.alternatives ?? []).find((entry) => entry.kind === 'radar')
  return alternative ? (alternative.data as RadarPlotData) : null
}

export function QuickStudyGridPlot(props: QuickStudyGridPlotProps) {
  const { result, view, statView = null, controlled = false, testIdPrefix = 'quick-study-grid' } = props

  const { t, i18n } = useTranslation('app')

  if (!quickStudyScorecardAdapter.accepts(result) && quickStudyCategoricalGridAdapter.accepts(result)) {
    return (
      <div data-testid={`${testIdPrefix}-stacked`}>
        <CategoricalSharePlotView
          plot={quickStudyCategoricalGridAdapter.read(result)}
          size="card"
          locale={i18n.language}
          testIdPrefix={`${testIdPrefix}-stacked-plot`}
          otherToggleLabels={{
            expand: (count: number) => t('studyReport.plotOther.expand', { count }),
            collapse: t('studyReport.plotOther.collapse'),
          }}
          showCounts
        />
      </div>
    )
  }
  const plot = stampedHeatGridOf(result)
  if (!plot) {
    return null
  }
  const radar = view === 'radar' ? radarOf(plot) : null

  if (radar) {
    return (
      <div data-testid={`${testIdPrefix}-radar`}>
        <RadarPlot data={radar} props={{ locale: i18n.language }} testIdPrefix={`${testIdPrefix}-radar-plot`} />
      </div>
    )
  }
  return (
    <div data-testid={`${testIdPrefix}-heat-grid`}>
      <HeatGridPlot
        data={plot.data}
        props={{ locale: i18n.language }}
        activeView={controlled && statView ? statView : undefined}
        showTabs={!controlled}
        testIdPrefix={`${testIdPrefix}-heat-grid-plot`}
      />
    </div>
  )
}
